import { router } from '@inertiajs/react'
import { useState } from 'react'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/Card'
import { Input } from '@/components/ui/Input'
import { Label } from '@/components/ui/Label'
import { Button } from '@/components/ui/Button'
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/Select'
import { SupportHoursEditor, SupportHoursSchedule } from '@/components/SupportHoursEditor'
import { useRouteParams } from '@/hooks/useRoutes'
import routes from '@/routeHelpers'

interface SupportHoursCardProps {
  schedule: SupportHoursSchedule | null
  timezone: string
  firstResponseSlaSeconds: number
}

const SLA_PRESETS: { label: string; seconds: number }[] = [
  { label: '2 min', seconds: 120 },
  { label: '5 min', seconds: 300 },
  { label: '15 min', seconds: 900 },
  { label: '1 hr', seconds: 3600 },
  { label: '4 hr', seconds: 14400 },
  { label: '24 hr', seconds: 86400 },
]

export function SupportHoursCard({ schedule, timezone, firstResponseSlaSeconds }: SupportHoursCardProps) {
  const { withAccount } = useRouteParams()
  const [slaInput, setSlaInput] = useState(secondsToInput(firstResponseSlaSeconds))
  const [slaUnit, setSlaUnit] = useState<'seconds' | 'minutes' | 'hours'>(unitFor(firstResponseSlaSeconds))
  const parsedSla = inputToSeconds(slaInput, slaUnit)

  const handleScheduleSave = (next: SupportHoursSchedule | null) => {
    router.patch(
      routes.settings.update.path(withAccount()),
      { account: { support_hours: next ?? {} }, tab: 'support-hours' },
      { preserveScroll: true },
    )
  }

  const handleSlaSave = () => {
    const seconds = inputToSeconds(slaInput, slaUnit)
    if (seconds == null) return
    router.patch(
      routes.settings.update.path(withAccount()),
      { account: { first_response_sla_seconds: seconds }, tab: 'support-hours' },
      { preserveScroll: true },
    )
  }

  const applyPreset = (seconds: number) => {
    setSlaUnit(unitFor(seconds))
    setSlaInput(secondsToInput(seconds))
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>First-response SLA</CardTitle>
          <CardDescription>
            Target time for an agent's first reply. Drives the SLA percentage on the analytics dashboard and the
            traffic-light colours on response-time cards.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-2">
              <Label htmlFor="sla-input">Target</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="sla-input"
                  type="number"
                  min={1}
                  value={slaInput}
                  onChange={(e) => setSlaInput(e.target.value)}
                  className="w-24"
                />
                <Select value={slaUnit} onValueChange={(value) => setSlaUnit(value as typeof slaUnit)}>
                  <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="seconds">seconds</SelectItem>
                    <SelectItem value="minutes">minutes</SelectItem>
                    <SelectItem value="hours">hours</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {parsedSla == null && (
                <p className="text-xs text-rose-500 dark:text-rose-400">
                  Enter between 10 seconds and 24 hours.
                </p>
              )}
            </div>
            <Button
              type="button"
              onClick={handleSlaSave}
              disabled={parsedSla == null || parsedSla === firstResponseSlaSeconds}
              className="cursor-pointer"
            >
              Save SLA
            </Button>
          </div>
          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <span>Common targets:</span>
            {SLA_PRESETS.map((p) => (
              <button
                key={p.seconds}
                type="button"
                onClick={() => applyPreset(p.seconds)}
                className="rounded-full border border-border px-2 py-0.5 hover:border-primary/50 cursor-pointer"
              >
                {p.label}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Support hours</CardTitle>
          <CardDescription>
            When your team is available to respond. Analytics use these to compute "business-hours" first-response time,
            so an after-hours message answered first thing the next morning doesn't show as 12 hours late.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="text-xs text-muted-foreground">
            Times are in <span className="font-medium">{timezone}</span> (account timezone). Leave empty to treat the
            account as 24/7 — relative response time will equal absolute.
          </div>
          <SupportHoursEditor value={schedule} onSave={handleScheduleSave} />
        </CardContent>
      </Card>
    </div>
  )
}

function unitFor(seconds: number): 'seconds' | 'minutes' | 'hours' {
  if (seconds >= 3600 && seconds % 3600 === 0) return 'hours'
  if (seconds >= 60 && seconds % 60 === 0) return 'minutes'
  return 'seconds'
}

function secondsToInput(seconds: number): string {
  const unit = unitFor(seconds)
  if (unit === 'hours') return String(seconds / 3600)
  if (unit === 'minutes') return String(seconds / 60)
  return String(seconds)
}

// Mirror the Rails validator (10..86400) so the Save button stays disabled
// when the user types something the server would reject.
const MIN_SLA_SECONDS = 10
const MAX_SLA_SECONDS = 86_400

function inputToSeconds(value: string, unit: 'seconds' | 'minutes' | 'hours'): number | null {
  const n = Number(value)
  if (!Number.isFinite(n) || n <= 0) return null
  const seconds = unit === 'hours' ? Math.round(n * 3600) : unit === 'minutes' ? Math.round(n * 60) : Math.round(n)
  if (seconds < MIN_SLA_SECONDS || seconds > MAX_SLA_SECONDS) return null
  return seconds
}
